import { useState, useEffect, useCallback, useMemo } from 'react';
import { s, authHeaders, Toast, StandardDataGrid, gridActionButton } from './listPageStyles';

const API_BASE = '/api/trainings';

const CATEGORIES = [
  { key: '', label: 'Tüm Kategoriler' },
  { key: 'ONBOARDING', label: 'Oryantasyon' },
  { key: 'PRODUCT', label: 'Ürün' },
  { key: 'SALES', label: 'Satış' },
  { key: 'SAFETY', label: 'Güvenlik' },
  { key: 'COMPLIANCE', label: 'Uyum' },
  { key: 'SOFT_SKILLS', label: 'Yetkinlik' },
];

const CATEGORY_LABEL = Object.fromEntries(CATEGORIES.filter(c => c.key).map(c => [c.key, c.label]));

const ACTIVE_TABS = [
  { key: '', label: 'Hepsi' },
  { key: 'true', label: 'Aktif' },
  { key: 'false', label: 'Pasif' },
];

const PAGE_SIZE_DEFAULT = 20;

const EMPTY_FORM = {
  textId: '',
  title: '',
  description: '',
  youtubeVideoId: '',
  durationSec: 0,
  category: 'PRODUCT',
  rewardPoints: 0,
  publishedAt: '',
  isActive: true,
};

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return undefined;
}

function unwrapResponse(payload) {
  const data = pick(payload, 'Data', 'data') ?? payload;
  return {
    items: pick(data, 'items', 'Items') ?? [],
    activeCount: pick(data, 'activeCount', 'ActiveCount') ?? 0,
    inactiveCount: pick(data, 'inactiveCount', 'InactiveCount') ?? 0,
  };
}

function formatDate(value) {
  if (!value) return '-';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return '-'; }
}

function formatDuration(sec) {
  const n = Number(sec);
  if (!Number.isFinite(n) || n <= 0) return '-';
  const m = Math.floor(n / 60);
  const s2 = n % 60;
  return `${m}:${String(s2).padStart(2, '0')}`;
}

function toLocalDateInput(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  } catch { return ''; }
}

function fromLocalDateInput(value) {
  if (!value) return null;
  try { return new Date(`${value}T00:00:00`).toISOString(); } catch { return null; }
}

function categoryPill(category) {
  const tones = {
    ONBOARDING: { bg: 'rgba(0, 115, 234, 0.10)', border: 'rgba(0, 115, 234, 0.32)', color: '#1d4ed8' },
    PRODUCT: { bg: 'rgba(253, 171, 61, 0.14)', border: 'rgba(253, 171, 61, 0.32)', color: '#92400e' },
    SALES: { bg: 'rgba(0, 200, 117, 0.12)', border: 'rgba(0, 200, 117, 0.30)', color: '#0f5132' },
    SAFETY: { bg: 'rgba(226, 68, 92, 0.12)', border: 'rgba(226, 68, 92, 0.30)', color: '#7f1d1d' },
    COMPLIANCE: { bg: 'rgba(139, 92, 246, 0.12)', border: 'rgba(139, 92, 246, 0.30)', color: '#5b21b6' },
    SOFT_SKILLS: { bg: 'rgba(20, 184, 166, 0.12)', border: 'rgba(20, 184, 166, 0.30)', color: '#115e59' },
  };
  const t = tones[category] || { bg: 'rgba(100, 116, 139, 0.10)', border: 'rgba(100, 116, 139, 0.28)', color: '#475569' };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      padding: '3px 9px', borderRadius: 999, fontWeight: 700, fontSize: 11,
      background: t.bg, border: `1px solid ${t.border}`, color: t.color,
    }}>
      {CATEGORY_LABEL[category] || category || '-'}
    </span>
  );
}

function activePill(isActive) {
  if (isActive) {
    return (
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '4px 10px', borderRadius: 999, fontWeight: 800, fontSize: 12,
        background: 'rgba(0, 200, 117, 0.12)', border: '1px solid rgba(0, 200, 117, 0.30)', color: '#0f5132',
      }}>
        <span style={{ width: 7, height: 7, borderRadius: 999, background: 'currentColor' }} />
        Aktif
      </span>
    );
  }
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '4px 10px', borderRadius: 999, fontWeight: 800, fontSize: 12,
      background: 'rgba(100, 116, 139, 0.14)', border: '1px solid rgba(100, 116, 139, 0.28)', color: '#64748b',
    }}>
      <span style={{ width: 7, height: 7, borderRadius: 999, background: 'currentColor' }} />
      Pasif
    </span>
  );
}

function badge(label, value, tone) {
  const tones = {
    blue: { bg: 'rgba(0, 115, 234, 0.10)', border: 'rgba(0, 115, 234, 0.32)', color: '#1d4ed8', dot: 'var(--accent)' },
    green: { bg: 'rgba(0, 200, 117, 0.12)', border: 'rgba(0, 200, 117, 0.32)', color: '#0f5132', dot: '#16a34a' },
    gray: { bg: 'rgba(100, 116, 139, 0.10)', border: 'rgba(100, 116, 139, 0.28)', color: '#475569', dot: '#64748b' },
  };
  const t = tones[tone] || tones.blue;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 8,
      padding: '7px 12px', borderRadius: 999,
      background: t.bg, border: `1px solid ${t.border}`, color: t.color,
      fontWeight: 800, fontSize: 12,
    }}>
      <span style={{ width: 7, height: 7, borderRadius: 999, background: t.dot }} />
      {label}: {value}
    </span>
  );
}

export function TrainingsPageLegacy() {
  const [category, setCategory] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const [searchText, setSearchText] = useState('');

  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ activeCount: 0, inactiveCount: 0 });
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT);
  const [sortField, setSortField] = useState('PublishedAt');
  const [sortDir, setSortDir] = useState('desc');
  const [selectedId, setSelectedId] = useState(null);

  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const showToast = useCallback((type, message) => {
    setToast({ type, message });
    window.setTimeout(() => setToast(null), 3000);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (category) params.set('category', category);
      if (activeFilter) params.set('isActive', activeFilter);
      params.set('take', '500');
      const res = await fetch(`${API_BASE}?${params.toString()}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Liste alınamadı (${res.status})`);
      const payload = await res.json();
      const data = unwrapResponse(payload);
      setRows(Array.isArray(data.items) ? data.items : []);
      setCounts({ activeCount: data.activeCount, inactiveCount: data.inactiveCount });
      setPage(1);
    } catch (err) {
      showToast('error', err.message || 'Liste alınamadı');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [category, activeFilter, showToast]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing({ mode: 'create' });
    setForm(EMPTY_FORM);
  };

  const openEdit = (row) => {
    setEditing({ mode: 'edit', id: pick(row, 'id', 'Id') });
    setForm({
      textId: pick(row, 'textId', 'TextId') || '',
      title: pick(row, 'title', 'Title') || '',
      description: pick(row, 'description', 'Description') || '',
      youtubeVideoId: pick(row, 'youtubeVideoId', 'YoutubeVideoId') || '',
      durationSec: pick(row, 'durationSec', 'DurationSec') ?? 0,
      category: pick(row, 'category', 'Category') || 'PRODUCT',
      rewardPoints: pick(row, 'rewardPoints', 'RewardPoints') ?? 0,
      publishedAt: toLocalDateInput(pick(row, 'publishedAt', 'PublishedAt')),
      isActive: pick(row, 'isActive', 'IsActive') ?? true,
    });
  };

  const closeEdit = () => { setEditing(null); setForm(EMPTY_FORM); };

  const submitForm = async () => {
    if (!form.textId.trim()) { showToast('error', 'TextId zorunlu.'); return; }
    if (!form.title.trim()) { showToast('error', 'Başlık zorunlu.'); return; }
    if (!form.category) { showToast('error', 'Kategori zorunlu.'); return; }

    setSaving(true);
    try {
      const body = {
        textId: form.textId.trim(),
        title: form.title.trim(),
        description: form.description.trim() || null,
        youtubeVideoId: form.youtubeVideoId.trim() || null,
        durationSec: Number(form.durationSec) || 0,
        category: form.category,
        rewardPoints: Number(form.rewardPoints) || 0,
        publishedAt: fromLocalDateInput(form.publishedAt),
        isActive: !!form.isActive,
      };
      const url = editing.mode === 'edit' ? `${API_BASE}/${editing.id}` : API_BASE;
      const method = editing.mode === 'edit' ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers: authHeaders(), body: JSON.stringify(body) });
      const payload = await res.json().catch(() => null);
      if (!res.ok) {
        const msg = pick(payload, 'Message', 'message') || `İşlem başarısız (${res.status})`;
        throw new Error(msg);
      }
      showToast('success', editing.mode === 'edit' ? 'Eğitim güncellendi.' : 'Eğitim oluşturuldu.');
      closeEdit();
      await load();
    } catch (err) {
      showToast('error', err.message || 'İşlem başarısız');
    } finally {
      setSaving(false);
    }
  };

  const submitDelete = async () => {
    if (!deleteRow) return;
    const id = pick(deleteRow, 'id', 'Id');
    setDeleteBusy(true);
    try {
      const res = await fetch(`${API_BASE}/${id}`, { method: 'DELETE', headers: authHeaders() });
      const payload = await res.json().catch(() => null);
      if (!res.ok) {
        const msg = pick(payload, 'Message', 'message') || `Silme başarısız (${res.status})`;
        throw new Error(msg);
      }
      showToast('success', 'Eğitim silindi.');
      setDeleteRow(null);
      await load();
    } catch (err) {
      showToast('error', err.message || 'Silme başarısız');
    } finally {
      setDeleteBusy(false);
    }
  };

  const filteredSorted = useMemo(() => {
    const needle = searchText.trim().toLocaleLowerCase('tr-TR');
    const match = (row) => {
      if (!needle) return true;
      const fields = [
        pick(row, 'textId', 'TextId'),
        pick(row, 'title', 'Title'),
        pick(row, 'description', 'Description'),
        pick(row, 'youtubeVideoId', 'YoutubeVideoId'),
      ];
      return fields.some(v => v && String(v).toLocaleLowerCase('tr-TR').includes(needle));
    };
    const filtered = rows.filter(match);
    const dir = sortDir === 'desc' ? -1 : 1;
    const sortKey = (row) => {
      switch (sortField) {
        case 'Title': return pick(row, 'title', 'Title') || '';
        case 'Category': return pick(row, 'category', 'Category') || '';
        case 'DurationSec': return Number(pick(row, 'durationSec', 'DurationSec')) || 0;
        case 'RewardPoints': return Number(pick(row, 'rewardPoints', 'RewardPoints')) || 0;
        case 'PublishedAt':
        default: {
          const v = pick(row, 'publishedAt', 'PublishedAt') || pick(row, 'createdDate', 'CreatedDate');
          return v ? new Date(v).getTime() : 0;
        }
      }
    };
    return [...filtered].sort((a, b) => {
      const av = sortKey(a);
      const bv = sortKey(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [rows, searchText, sortField, sortDir]);

  const pagedRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredSorted.slice(start, start + pageSize);
  }, [filteredSorted, page, pageSize]);

  const columns = useMemo(() => [
    {
      field: 'title',
      headerName: 'Başlık',
      flex: 1.6,
      minWidth: 240,
      renderCell: ({ row }) => (
        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
          <span style={{ fontWeight: 700 }}>{pick(row, 'title', 'Title') || '-'}</span>
          <span style={{ fontSize: 11, color: '#6b7280', fontFamily: 'monospace' }}>
            {pick(row, 'textId', 'TextId') || '-'}
          </span>
        </div>
      ),
    },
    {
      field: 'category',
      headerName: 'Kategori',
      flex: 0.8,
      minWidth: 130,
      renderCell: ({ row }) => categoryPill(pick(row, 'category', 'Category')),
    },
    {
      field: 'durationSec',
      headerName: 'Süre',
      flex: 0.5,
      minWidth: 90,
      valueGetter: ({ row }) => formatDuration(pick(row, 'durationSec', 'DurationSec')),
    },
    {
      field: 'rewardPoints',
      headerName: 'Puan',
      flex: 0.5,
      minWidth: 80,
      valueGetter: ({ row }) => pick(row, 'rewardPoints', 'RewardPoints') ?? 0,
    },
    {
      field: 'publishedAt',
      headerName: 'Yayın',
      flex: 0.7,
      minWidth: 110,
      valueGetter: ({ row }) => formatDate(pick(row, 'publishedAt', 'PublishedAt')),
    },
    {
      field: 'isActive',
      headerName: 'Durum',
      flex: 0.6,
      minWidth: 100,
      renderCell: ({ row }) => activePill(pick(row, 'isActive', 'IsActive')),
    },
    {
      field: 'actions',
      headerName: 'Aksiyon',
      flex: 0.9,
      minWidth: 180,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <div style={{ display: 'flex', gap: 6 }} onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            style={{ ...gridActionButton, background: 'var(--warn)' }}
            onClick={() => openEdit(row)}
          >
            Düzenle
          </button>
          <button
            type="button"
            style={{ ...gridActionButton, background: '#dc2626' }}
            onClick={() => setDeleteRow(row)}
          >
            Sil
          </button>
        </div>
      ),
    },
  ], []);

  const totalCount = filteredSorted.length;

  return (
    <div style={s.container}>
      <div style={s.pageHeader}>
        <div style={s.pageHeaderText}>
          <h1 style={s.pageTitle}>Eğitimler</h1>
          <p style={s.pageSubtitle}>
            Saha temsilcisine atanan eğitim videoları. Tamamlananlar KazandıRio puanı kazandırır.
            Mobile master delta ile çeker.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          {badge('Aktif', counts.activeCount, 'green')}
          {badge('Pasif', counts.inactiveCount, 'gray')}
          <button type="button" style={s.btnSecondary} onClick={load} disabled={loading}>
            {loading ? 'Yükleniyor...' : 'Yenile'}
          </button>
          <button type="button" style={s.btnPrimary} onClick={openCreate}>
            + Yeni Eğitim
          </button>
        </div>
      </div>

      <div style={s.toolbar}>
        <div style={s.toolbarLeft}>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            style={s.select}
          >
            {CATEGORIES.map(c => <option key={c.key || 'all'} value={c.key}>{c.label}</option>)}
          </select>
          <span style={{ width: 1, height: 22, background: '#e5e7eb', margin: '0 4px' }} />
          {ACTIVE_TABS.map(tab => (
            <button
              key={tab.key || 'all'}
              type="button"
              onClick={() => setActiveFilter(tab.key)}
              style={{
                ...(activeFilter === tab.key ? s.btnPrimary : s.btnSecondary),
                padding: '6px 14px', fontSize: 13,
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div style={s.toolbarRight}>
          <input
            type="search"
            value={searchText}
            onChange={(e) => { setSearchText(e.target.value); setPage(1); }}
            placeholder="Başlık / textId / açıklama..."
            style={s.filterInput}
          />
        </div>
      </div>

      <StandardDataGrid
        rows={pagedRows}
        columns={columns}
        totalCount={totalCount}
        loading={loading}
        pageSize={pageSize}
        currentPage={page}
        onPageChange={setPage}
        onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
        sortField={sortField}
        sortDir={sortDir}
        onSortChange={(field, dir) => { setSortField(field); setSortDir(dir); }}
        selectedId={selectedId}
        onRowClick={(row) => setSelectedId(pick(row, 'id', 'Id'))}
        onRowDoubleClick={openEdit}
        exportFileName="egitimler"
      />

      {editing && (
        <div style={s.overlay} onClick={closeEdit}>
          <div style={{ ...s.modal, maxWidth: 640 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h2 style={s.modalTitle}>
                {editing.mode === 'edit' ? 'Eğitimi Düzenle' : 'Yeni Eğitim'}
              </h2>
              <button type="button" style={s.closeBtn} onClick={closeEdit}>×</button>
            </div>
            <div style={s.modalBody}>
              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>TextId *</label>
                  <input
                    type="text"
                    value={form.textId}
                    onChange={(e) => setForm({ ...form, textId: e.target.value })}
                    placeholder="örn. tr-product-101"
                    style={s.input}
                  />
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Kategori *</label>
                  <select
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    style={s.select}
                  >
                    {CATEGORIES.filter(c => c.key).map(c => (
                      <option key={c.key} value={c.key}>{c.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>Başlık *</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  style={s.input}
                />
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>Açıklama</label>
                <textarea
                  rows={3}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  style={s.textarea}
                />
              </div>
              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>YouTube Video ID</label>
                  <input
                    type="text"
                    value={form.youtubeVideoId}
                    onChange={(e) => setForm({ ...form, youtubeVideoId: e.target.value })}
                    placeholder="dQw4w9WgXcQ"
                    style={s.input}
                  />
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Süre (saniye)</label>
                  <input
                    type="number"
                    min="0"
                    value={form.durationSec}
                    onChange={(e) => setForm({ ...form, durationSec: e.target.value })}
                    style={s.input}
                  />
                </div>
              </div>
              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Ödül Puanı</label>
                  <input
                    type="number"
                    min="0"
                    value={form.rewardPoints}
                    onChange={(e) => setForm({ ...form, rewardPoints: e.target.value })}
                    style={s.input}
                  />
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Yayın Tarihi</label>
                  <input
                    type="date"
                    value={form.publishedAt}
                    onChange={(e) => setForm({ ...form, publishedAt: e.target.value })}
                    style={s.input}
                  />
                </div>
              </div>
              <div style={s.formGroup}>
                <label style={{ ...s.label, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={form.isActive}
                    onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                  />
                  Aktif (mobile'da görünür)
                </label>
              </div>
            </div>
            <div style={s.modalFooter}>
              <button type="button" style={s.btnSecondary} onClick={closeEdit} disabled={saving}>
                Vazgeç
              </button>
              <button
                type="button"
                style={{ ...s.btnPrimary, opacity: saving ? 0.6 : 1 }}
                onClick={submitForm}
                disabled={saving}
              >
                {saving ? 'Kaydediliyor...' : (editing.mode === 'edit' ? 'Güncelle' : 'Oluştur')}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteRow && (
        <div style={s.overlay} onClick={() => setDeleteRow(null)}>
          <div style={s.modal} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h2 style={s.modalTitle}>Eğitimi Sil</h2>
              <button type="button" style={s.closeBtn} onClick={() => setDeleteRow(null)}>×</button>
            </div>
            <div style={s.modalBody}>
              <div style={{ fontSize: 14, color: '#1f2937' }}>
                <strong>{pick(deleteRow, 'title', 'Title')}</strong> eğitimini silmek istediğinize emin misiniz?
              </div>
              <div style={{ fontSize: 12, color: '#dc2626', marginTop: 8 }}>
                Bu işlem geri alınamaz. Mobile'da bu eğitim bir sonraki sync'te kaybolacak.
              </div>
            </div>
            <div style={s.modalFooter}>
              <button type="button" style={s.btnSecondary} onClick={() => setDeleteRow(null)} disabled={deleteBusy}>
                Vazgeç
              </button>
              <button
                type="button"
                style={{ ...s.btnDanger, opacity: deleteBusy ? 0.6 : 1 }}
                onClick={submitDelete}
                disabled={deleteBusy}
              >
                {deleteBusy ? 'Siliniyor...' : 'Sil'}
              </button>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
}

export default TrainingsPageLegacy;
